import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getAuthenticatedApiClient } from "../../../api/apiClient";
import type {
  ApproveBrokerOnboardingRequest,
  CreateBrokerRequest,
  RejectBrokerOnboardingRequest
} from "../../../api/Api";
import { OnboardingStatusFilter } from "../../../components/onboardingRequests/types";
import { brokerKeys } from "../../brokers/dataHooks/brokerQueries";

export const brokerOnboardingRequestKeys = {
  all: ["brokerOnboardingRequests"] as const,
  lists: () => [...brokerOnboardingRequestKeys.all, "list"] as const,
  list: (status: OnboardingStatusFilter = "Pending") =>
    [...brokerOnboardingRequestKeys.lists(), status] as const,
  mine: () => [...brokerOnboardingRequestKeys.all, "mine"] as const
};

export const useBrokerOnboardingRequests = (
  statusFilter: OnboardingStatusFilter = "Pending",
  options?: { refetchInterval?: number }
) => {
  return useQuery({
    queryKey: brokerOnboardingRequestKeys.list(statusFilter),
    queryFn: async () => {
      const apiClient = await getAuthenticatedApiClient();
      const response = await apiClient.brokerOnboardingRequests.list({
        status: statusFilter
      });
      return response.data;
    },
    refetchInterval: options?.refetchInterval
  });
};

export const useMyBrokerOnboardingRequests = () => {
  return useQuery({
    queryKey: brokerOnboardingRequestKeys.mine(),
    queryFn: async () => {
      const apiClient = await getAuthenticatedApiClient();
      const response = await apiClient.brokerOnboardingRequests.mine();
      return response.data;
    }
  });
};

type MutationCallbacks = {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
};

export const useSubmitBrokerOnboardingRequest = (
  options?: MutationCallbacks
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: CreateBrokerRequest) => {
      const apiClient = await getAuthenticatedApiClient();
      const response = await apiClient.brokerOnboardingRequests.submit(body);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: brokerOnboardingRequestKeys.all
      });
      options?.onSuccess?.();
    },
    onError: (error) => options?.onError?.(error as Error)
  });
};

export const useApproveBrokerOnboardingRequest = (
  options?: MutationCallbacks
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (vars: {
      id: string;
      body: ApproveBrokerOnboardingRequest;
    }) => {
      const apiClient = await getAuthenticatedApiClient();
      const response = await apiClient.brokerOnboardingRequests.approve(
        vars.id,
        vars.body
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: brokerOnboardingRequestKeys.all
      });
      queryClient.invalidateQueries({ queryKey: brokerKeys.all });
      options?.onSuccess?.();
    },
    onError: (error) => options?.onError?.(error as Error)
  });
};

export const useRejectBrokerOnboardingRequest = (
  options?: MutationCallbacks
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (vars: {
      id: string;
      body: RejectBrokerOnboardingRequest;
    }) => {
      const apiClient = await getAuthenticatedApiClient();
      const response = await apiClient.brokerOnboardingRequests.reject(
        vars.id,
        vars.body
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: brokerOnboardingRequestKeys.all
      });
      options?.onSuccess?.();
    },
    onError: (error) => options?.onError?.(error as Error)
  });
};
